import { useState } from "react";
import type { TranslateFn } from "../../i18n";
import type { SocialProvider } from "../utils/profile-auth";
import googleIcon from "../assets/social/google.svg?url";
import profileSyncIllustration from "../assets/social/profile-sync-illustration.svg?url";

const appleIcon = "data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%3e%3cpath%20fill='%23111111'%20d='M16.37%201.43c0%201.14-.46%202.23-1.19%203.05-.8.91-2.13%201.61-3.2%201.5-.14-1.09.4-2.25%201.12-3.06.8-.9%202.2-1.58%203.27-1.49zM20.2%2017.13c-.56%201.27-.83%201.84-1.55%202.96-1.01%201.56-2.44%203.5-4.21%203.52-1.58.02-1.98-1.03-4.12-1.02-2.14.01-2.59%201.05-4.17%201.03-1.77-.02-3.12-1.77-4.13-3.33-2.82-4.36-3.12-9.47-1.38-12.19%201.23-1.94%203.18-3.08%205.01-3.08%201.86%200%203.03%201.03%204.57%201.03%201.49%200%202.39-1.03%204.54-1.03%201.62%200%203.34.88%204.56%202.41-4%202.19-3.35%207.9.88%209.7z'/%3e%3c/svg%3e";
const linkedinIcon = "data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%3e%3crect%20width='22'%20height='22'%20x='1'%20y='1'%20rx='4'%20fill='%230A66C2'/%3e%3cpath%20fill='%23fff'%20d='M7.3%209.8h-3v9h3v-9zM5.8%208.6c1%200%201.7-.7%201.7-1.6S6.8%205.4%205.8%205.4%204.1%206.1%204.1%207s.7%201.6%201.7%201.6zm4.1%201.2v9h3v-5c0-.3%200-.5.1-.7.2-.5.7-1.1%201.5-1.1%201.1%200%201.5.8%201.5%202v4.8h3v-5.2c0-2.8-1.5-4.1-3.5-4.1-1.6%200-2.3.9-2.7%201.5v-1.2h-2.9z'/%3e%3c/svg%3e";

interface ProviderOption {
	id: SocialProvider;
	label: string;
	shortLabel: string;
	icon: string;
}

interface SocialProfileAuthViewProps {
	loadingProvider: SocialProvider | null;
	error: string | null;
	onLogin: (provider: SocialProvider) => void;
	t: TranslateFn;
}

const PROVIDERS: ProviderOption[] = [
	{ id: "google", label: "Continue with Google", shortLabel: "Google", icon: googleIcon },
	{ id: "apple", label: "Continue with Apple", shortLabel: "Apple", icon: appleIcon },
	{ id: "linkedin", label: "Continue with LinkedIn", shortLabel: "LinkedIn", icon: linkedinIcon },
];

export function SocialProfileAuthView({ loadingProvider, error, onLogin, t }: SocialProfileAuthViewProps) {
	const [consentAccepted, setConsentAccepted] = useState(false);

	return (
		<div className="ps-profile-auth">
			<div className="ps-profile-auth-shell">
				<section className="ps-profile-auth-story" aria-label={t("PrimeStyleAI profile benefits")}>
					<div className="ps-profile-auth-visual">
						<img src={profileSyncIllustration} alt="" aria-hidden="true" />
					</div>
						<div className="ps-profile-auth-story-copy">
							<span className="ps-profile-auth-badge">{t("PrimeStyleAI profile")}</span>
							<h2 className="ps-profile-auth-title">{t("Create your fit profile once.")}</h2>
							<p className="ps-profile-auth-copy">
								{t("Securely reuse your measurements and photo across PrimeStyleAI experiences and partner stores.")}
							</p>
						</div>
				</section>

				<section className="ps-profile-auth-card" aria-label={t("Social sign in")}>
					<div className="ps-profile-auth-card-header">
						<span className="ps-profile-auth-card-kicker">{t("Sign in")}</span>
						<h3 className="ps-profile-auth-card-title">{t("Choose one account")}</h3>
						<p className="ps-profile-auth-card-copy">
							{t("No password needed. After sign in, you will continue to the normal profile setup.")}
						</p>
					</div>

					<div className="ps-profile-auth-icon-row">
					{PROVIDERS.map((provider) => {
						const isLoading = loadingProvider === provider.id;
						return (
							<button
								key={provider.id}
								type="button"
								className={`ps-profile-auth-button ps-provider-${provider.id}${!consentAccepted ? " ps-consent-required" : ""}`}
								disabled={!consentAccepted || loadingProvider !== null}
								onClick={() => onLogin(provider.id)}
								aria-label={t(provider.label)}
								title={t(provider.label)}
							>
								<img className="ps-profile-auth-icon" src={provider.icon} alt="" aria-hidden="true" />
								<span>{isLoading ? t("Opening...") : t(provider.shortLabel)}</span>
							</button>
						);
					})}
					</div>

					<label className="ps-profile-auth-consent">
						<input
							type="checkbox"
							checked={consentAccepted}
							onChange={(event) => setConsentAccepted(event.currentTarget.checked)}
						/>
						<span>
							{t("I agree to the")}{" "}
							<a href="https://primestyleai.com/terms" target="_blank" rel="noreferrer">
								{t("Terms of Service")}
							</a>{" "}
							{t("and acknowledge the")}{" "}
							<a href="https://primestyleai.com/privacy-policy" target="_blank" rel="noreferrer">
								{t("Privacy Policy")}
							</a>
							{t(". I understand that my profile photo and measurements may be stored by PrimeStyleAI to provide cross-site sizing recommendations and virtual try-on services, and that recommendations and measurements are estimates and not guarantees.")}
						</span>
					</label>

					{error && <p className="ps-profile-auth-error">{error}</p>}
					<p className="ps-profile-auth-note">
						{t("Your profile is private to your PrimeStyleAI account and can be updated anytime.")}
					</p>
				</section>
			</div>
		</div>
	);
}
